import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import { Toaster } from "./components/ui/sonner";
import LandingPage from "./pages/LandingPage";
import Dashboard from "./pages/Dashboard";
import UserDashboard from "./pages/UserDashboard";
import Chat from "./pages/Chat";
import Subscription from "./pages/Subscription";
import Profile from "./pages/Profile";
import AuthPage from "./pages/AuthPage";
import Onboarding from "./pages/Onboarding";
import Results from "./pages/Results";
import About from "./pages/About";
import Portfolio from "./pages/Portfolio";
import MarketsPage from "./pages/MarketsPage";
import SupportButton from "./components/SupportButton";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import "./App.css";

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="animate-pulse text-zinc-400">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/auth" replace />;
  }
  
  return children;
};

// Onboarding Check Route
const OnboardingRoute = ({ children }) => {
  const { user } = useAuth();
  
  // If user hasn't completed onboarding, redirect to onboarding
  if (user && !user.onboarding_completed) {
    return <Navigate to="/onboarding" replace />;
  }
  
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored auth
    const token = localStorage.getItem("token");
    const storedUser = localStorage.getItem("user");
    
    if (token && storedUser) {
      setUser(JSON.parse(storedUser));
    }
    setLoading(false);
    
    // Register Service Worker for PWA
    if ('serviceWorker' in navigator) {
      window.addEventListener('load', () => {
        navigator.serviceWorker.register('/service-worker.js')
          .then((registration) => {
            console.log('ServiceWorker registered:', registration.scope);
          })
          .catch((error) => {
            console.log('ServiceWorker registration failed:', error);
          });
      });
    }
  }, []);

  const login = (token, userData) => {
    localStorage.setItem("token", token);
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  const updateUser = (userData) => {
    localStorage.setItem("user", JSON.stringify(userData));
    setUser(userData);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, loading }}>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<About />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route 
              path="/onboarding" 
              element={
                <ProtectedRoute>
                  <Onboarding />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  <OnboardingRoute>
                    <UserDashboard />
                  </OnboardingRoute>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/markets" 
              element={<MarketsPage />} 
            />
            {/* LIONAI Chat - temporarily disabled for cost optimization */}
            <Route 
              path="/chat" 
              element={<Navigate to="/dashboard" replace />} 
            />
            <Route 
              path="/portfolio" 
              element={
                <ProtectedRoute>
                  <OnboardingRoute>
                    <Portfolio />
                  </OnboardingRoute>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/subscription" 
              element={
                <ProtectedRoute>
                  <Subscription />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <OnboardingRoute>
                    <Profile />
                  </OnboardingRoute>
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/results" 
              element={
                <ProtectedRoute>
                  <OnboardingRoute>
                    <Results />
                  </OnboardingRoute>
                </ProtectedRoute>
              } 
            />
          </Routes>
        </BrowserRouter>
        <SupportButton />
        <PWAInstallPrompt />
        <Toaster position="top-right" />
      </div>
    </AuthContext.Provider>
  );
}

export default App;
